import _ from 'lodash';
import { html, css } from '~/lib/bn-lit-element';
import parseSiren from 'siren-parser';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { renderIf, SirenActionMixin, LitEntity } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

class BinImportSummary extends SirenActionMixin(LitEntity) {
  static get properties() {
    return {
      requestingDeviceInstallation: { type: Boolean, attribute: false },
      successfulRequestCount: { type: Number, attribute: false },
      importItemsIndexHref: { type: String, attribute: false },
      importItemsHref: { type: String, attribute: false },
      binSummary: { type: Object, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      bn-surface {
        margin: 1em 0 0 0;
      }
    `];
  }

  render() {
    if (!this.entity) {
      return html``;
    }

    const { state } = this.entity.properties;
    if (state !== 'completed') {
      return html``;
    }

    return html`
      <h2>Imported Bin Summary</h2>
      <lit-entity href="${this.importItemsIndexHref}" @update="${this._onImportItemsIndexUpdate}"></lit-entity>
      <lit-entity href="${this.importItemsHref}" @update="${this._onImportItemsUpdate}"></lit-entity>
      <bn-surface>
        ${renderIf(!this.binSummary, () => html`
          <bn-loading></bn-loading>
        `)}
        ${renderIf(this.binSummary, () => html`
          <div>
            <bn-property label="Monitored Bins">${this.binSummary.monitored.size}</bn-property>
            ${renderIf(this.binSummary.unmonitored.size, () => html`
              <bn-property label="Unmonitored Bins">${this.binSummary.unmonitored.size}</bn-property>
            `)}
            ${renderIf(this.binSummary.deleted.size, () => html`
              <bn-property label="Deleted Bins">${this.binSummary.deleted.size}</bn-property>
            `)}
            <div>
              <bn-property label="Inactive Bins">${this.binSummary.inactive.size}</bn-property>
              <div>
                <button class="button button--contained"
                        ?disabled=${this.requestingDeviceInstallation || this.binSummary.inactive.size === 0}
                        @click="${() => this._requestDeviceInstallation()}">
                  Request Device Installation
                </button>
                ${renderIf(this.requestingDeviceInstallation, () => html`
                  <bn-icon name="fas fa-spinner fa-pulse"></bn-icon>
                `)}
                ${renderIf(!this.requestingDeviceInstallation && this.successfulRequestCount !== undefined, () => html`
                  <span>${this.successfulRequestCount} successful requests</span>
                `)}
              </div>
            </div>
          </div>
        `)}
      </bn-surface>
    `;
  }
  async _requestDeviceInstallation() {
    this.requestingDeviceInstallation = true;
    let pageHref = this.importItemsHref;
    let successfulRequestCount = 0;
    for (;;) {
      if (!pageHref) {
        break;
      }
      const importItems = parseSiren((await entityStore.fetchWait(pageHref)).entity);
      const requestResults = await Promise.all(importItems.entities.map(async (importItem) => {
        const binHref = importItem.getLink('related')?.href;
        const binEntity = await this.getBinEntity(binHref);
        if (binEntity?.properties.monitoringType !== 'inactive') {
          return false;
        }
        await this.submitAction(binEntity.getAction('update-monitoring-type'), { monitoringType: 'monitored' });
        return true;
      }));
      successfulRequestCount += _.sum(requestResults);
      pageHref = importItems.getLink('next')?.href;
    }
    entityStore.invalidateHref(this.importItemsHref);
    this.successfulRequestCount = successfulRequestCount;
    this.requestingDeviceInstallation = false;
  }

  async getBinEntity(binHref) {
    if (!binHref) {
      return;
    }
    try {
      const { entity: binEntity } = await entityStore.fetchWait(binHref);
      return parseSiren(binEntity);
    } catch (e) {
      if (e.error?.code === 404) {
        return;
      }
      throw e;
    }
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.importItemsIndexHref = this.getHref('https://api.binsentry.com/rel/import-items');
    this.importItemsHref = undefined;
    this.binSummary = undefined;

    this.successfulRequestCount = undefined;
  }

  _onImportItemsIndexUpdate(e) {
    const importItemsIndex = parseSiren(e.detail.entity);
    const filterAction = importItemsIndex.getAction('filter-import-items');
    this.importItemsHref = this.parseAction(filterAction, {
      limit: 20,
      itemType: 'bin',
    }).url.href;
  }

  async _onImportItemsUpdate(e) {
    if (!e.detail.entity) {
      this.binSummary = undefined;
      return;
    }

    const binSummary = {
      inactive: new Set(),
      monitored: new Set(),
      unmonitored: new Set(),
      deleted: new Set(),
    };

    let page = parseSiren(e.detail.entity);
    for (;;) {
      if (!page) {
        break;
      }
      const bins = await Promise.all(page.entities.map(async (importItem) => {
        const binHref = importItem.getLink('related')?.href;
        const binEntity = await this.getBinEntity(binHref);
        return { binHref, binEntity };
      }));
      for (const { binHref, binEntity } of bins) {
        if (!binHref) {
          continue;
        }
        if (!binEntity) {
          binSummary.deleted.add(binHref);
          continue;
        }
        binSummary[binEntity.properties.monitoringType].add(binHref);
      }
      const nextHref = page.getLink('next')?.href;
      page = nextHref ? parseSiren((await entityStore.fetchWait(nextHref)).entity) : undefined;
    }
    this.binSummary = binSummary;
  }
}

window.customElements.define('bin-import-summary', BinImportSummary);
